import React, { useEffect, useMemo, useState } from 'react';
import {
  useNavigate,
  useParams,
  Link,
} from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Row,
  Form,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { get, postJson, putJson } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';
import SectionReferenti from './Referenti'
import CompAbilitato from "../../../Components/Forms/RadioAbilitato";

type FormState = {
  ragione_sociale: string;
  partita_iva: string;
  nome: string;
  telefono: string;
  cognome: string;
  abilitato: 1 | 0;
  codice_sdi: string;
  pec: string;
  email: string;
  indirizzo: string;
  provincia: string;
  civico: string;
  cap: string;
  citta: string;
};

type Provincia = {
  ID: string;
  nome: string;
};

type Cliente = {
  ID: number;
  ragionesociale: string | null;
  nome: string | null;
  cognome: string | null;
  abilitato: 1 | 0;
  email: string;
  telefono: string | null;
  piva: string | null;
  cf: string | null;
  indirizzo: string | null;
  civico: string | null;
  citta: string | null;
  cap: string | null;
  provinciaID: string | number | null;
};

type Indirizzo = {
  ID: number;
  clienteID: number;
  alias: string;
  referente: string;
  indirizzo: string | null;
  note: string | null;
  civico: string | null;
  citta: string | null;
  provinciaID: string | null;
  cap: string | null;
  nazioneID: string | null;
  cancellato: string;
  cancellato_data: string | null;
  cancellato_user: string | null;
  indirizzo_google: string | null;
  telefono: string | null;
};

type Progetto = {
  ID: number;
  nome: string;
};

type ClienteDetailResponse = {
  cliente: { [key: string]: any };
  indirizzo?: Indirizzo;
  progetti?: Progetto[];
};

export default function UserForm() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const isEdit = slug && slug !== 'new';
  const id = isEdit ? Number(slug) : null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [province, setProvincie] = useState<Provincia[]>([]);
  const [progetti, setProgetti] = useState<Progetto[]>([]);

  const [form, setForm] = useState<FormState>({
    ragione_sociale: '',
    partita_iva: '',
    codice_sdi: '',
    nome: '',
    cognome: '',
    abilitato: 0,
    telefono: '',
    pec: '',
    email: '',
    provincia: '',
    indirizzo: '',
    civico: '',
    cap: '',
    citta: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await get<any>('/data/province');
        const payload = (res as any)?.data ?? res;
        setProvincie(Array.isArray(payload) ? payload : []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchData();
  }, []);


  useEffect(() => {
    if (!isEdit || !id) return;

    (async () => {
      try {
        setLoading(true);

        const res = await get<ClienteDetailResponse>(`/clienti/detail?ID=${id}`);
        const data = (res as any)?.data ?? res;

        const rawCliente = data.cliente as { [key: string]: any };
        const indirizzo = data.indirizzo as Indirizzo | undefined;
        const projects = Array.isArray(data.progetti) ? data.progetti : [];

        const cliente = Object.fromEntries(
          Object.entries(rawCliente).filter(([key]) => isNaN(Number(key)))
        ) as Cliente;

        setForm({
          ragione_sociale: cliente.ragionesociale ?? '',
          partita_iva: cliente.piva ?? '',
          nome: cliente.nome ?? '',
          cognome: cliente.cognome ?? '',
          telefono: cliente.telefono ?? '',
          abilitato: cliente.abilitato ?? 0,
          codice_sdi: (rawCliente.codice_sdi as string) ?? '',
          pec: (rawCliente.pec as string) ?? '',
          email: (rawCliente.email as string) ?? '',
          indirizzo: indirizzo?.indirizzo ?? '',
          provincia: (indirizzo?.provinciaID as string) ?? '',
          civico: cliente.civico ?? indirizzo?.civico ?? '',
          cap: cliente.cap ?? indirizzo?.cap ?? '',
          citta: cliente.citta ?? indirizzo?.citta ?? '',
        });
        setProgetti(projects);
      } catch (e: any) {
        console.error(e);
        toast.error('Errore nel caricamento cliente');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id]);


  const canSubmit = useMemo(() => {
    return !!form.ragione_sociale.trim();
  }, [form.ragione_sociale]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) {
      toast.warn('Compila almeno la ragione sociale.');
      return;
    }

    const payload: any = {
      ragione_sociale: form.ragione_sociale || null,
      partita_iva: form.partita_iva || null,
      codice_sdi: form.codice_sdi || null,
      cognome: form.cognome || null,
      abilitato: form.abilitato || 0,
      telefono: form.telefono || null,
      nome: form.nome || null,
      pec: form.pec || null,
      email: form.email || null,
      indirizzo: form.indirizzo || null,
      civico: form.civico || null,
      provincia: form.provincia || null,
      cap: form.cap || null,
      citta: form.citta || null,
    };

    try {
      setSaving(true);
      if (isEdit && id) {
        await putJson(`/clienti/edit?ID=${id}`, payload);
        toast.success('Cliente aggiornato con successo');
      } else {
        const res = await postJson('/clienti/create', payload);
        const body: any = (res as any)?.data ?? res;
        const newId = Number(body.id ?? body.ID ?? body.clienteID);
        toast.success('Cliente creato con successo');
        // if (newId) {
        //   navigate(`/gestione-clienti/${encodeURIComponent(String(newId))}`);
        // } else {
          navigate('/gestione-clienti');
        // }
        return;
      }
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ?? e?.message ?? 'Errore nel salvataggio';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page-body pt-4">
        <Card>
          <CardBody className="d-flex justify-content-center py-5">
            <Spinner />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-body pt-4">
      <Form onSubmit={onSubmit} className='position-relative'>
        {/* CARD 1 - TITOLO / TOOLBAR */}
        <Col className='mt-3' xl={12}>
          <div>
            <Card>
              <CardBody className="d-flex justify-content-between align-items-center">
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center">
                    <H3 className="mb-0">
                      {isEdit ? 'Modifica Cliente' : 'Crea Nuovo Cliente'}
                    </H3>
                    <div className='d-flex flex-row gap-2'>
                      <Btn
                        color="primary"
                        className="px-4"
                        type="submit"
                        disabled={!canSubmit || saving}
                      >
                        {saving ? (
                          <Spinner size="sm" />
                        ) : isEdit ? (
                          'Aggiorna'
                        ) : (
                          'Crea'
                        )}
                      </Btn>
                      <Link to="/gestione-clienti">
                        <Btn className="px-4" color="secondary" type="button">
                          Torna Indietro
                        </Btn>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </Col>
        <Card>
          <CardBody>
            <H3>Progetti Associati</H3>
            <Row>
              <Col lg={12}>
                <div className="w-100">
                  {isEdit && progetti.length > 0 && (
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {progetti.map(p => (
                        <Btn
                          key={p.ID}
                          color="light"
                          className="px-3 btn-light"
                          type="button"
                          onClick={() => navigate(`/progetti/${p.ID}`)}
                        >
                          {p.nome}
                        </Btn>
                      ))}
                    </div>
                  )}
                </div>
              </Col>

            </Row>
          </CardBody>
        </Card>

        {/* CARD 2 - ANAGRAFE CLIENTE */}
        <Col xl={12} className="mb-3">
          <Card>
            <CardBody>
              <H3 className="mb-3">Anagrafe Cliente</H3>
              <Row className="g-3">
                <Col lg={12}>
                  <Label>Ragione sociale</Label>
                  <Input
                    name="ragione_sociale"
                    value={form.ragione_sociale}
                    onChange={onChange}
                    placeholder="Ragione sociale"
                  />
                </Col>
                <Col lg={6}>
                  <Label>Partita IVA</Label>
                  <Input
                    name="partita_iva"
                    value={form.partita_iva}
                    onChange={onChange}
                    placeholder="Partita IVA"
                  />
                </Col>
                <Col lg={6}>
                  <Label>Codice SDI</Label>
                  <Input
                    name="codice_sdi"
                    value={form.codice_sdi}
                    onChange={onChange}
                    placeholder="Codice SDI"
                  />
                </Col>
                <Col lg={6}>
                  <Label>PEC</Label>
                  <Input
                    type="email"
                    name="pec"
                    value={form.pec}
                    onChange={onChange}
                    placeholder="indirizzo@pec.it"
                  />
                </Col>
                <Col lg={6}>
                  <Label>E-mail</Label>
                  <Input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="email@pec.it"
                  />
                </Col>

                 <Col xs={6}>
                  <CompAbilitato
                    value={form.abilitato}
                    onChange={(v) =>
                      setForm((p) => ({ ...p, abilitato: v }))
                    }
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl={12} className="mb-3">
          <Card>
            <CardBody>
              <H3 className="mb-3">Titolare</H3>
              <Row className="g-3">
                <Col lg={6}>
                  <Label>Nome</Label>
                  <Input
                    name="nome"
                    value={form.nome}
                    onChange={onChange}
                    placeholder="Nome"
                  />
                </Col>
                <Col lg={6}>
                  <Label>cognome</Label>
                  <Input
                    name="cognome"
                    value={form.cognome}
                    onChange={onChange}
                    placeholder="Cognome"
                  />
                </Col>
                <Col lg={6}>
                  <Label>Telefono</Label>
                  <Input
                    name="telefono"
                    value={form.telefono}
                    onChange={onChange}
                    placeholder="1234567890"
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* CARD 3 - INDIRIZZO */}
        <Col xl={12} className="mb-3">
          <Card>
            <CardBody>
              <H3 className="mb-3">Indirizzo</H3>
              <Row className="g-3">
                <Col lg={10}>
                  <Label>Indirizzo</Label>
                  <Input
                    name="indirizzo"
                    placeholder="Via / Piazza..."
                    value={form.indirizzo}
                    onChange={onChange}
                  />
                </Col>
                <Col lg={2}>
                  <Label>Civico</Label>
                  <Input
                    name="civico"
                    placeholder="N."
                    value={form.civico}
                    onChange={onChange}
                  />
                </Col>
                <Col lg={4}>
                  <Label>Provincia</Label>
                  <Input
                    type="select"
                    name="provincia"
                    value={form.provincia}
                    onChange={onChange}
                  >
                    <option value={''}>Scegli</option>
                    {province.map(g => (
                      <option key={g.ID} value={g.ID}>
                        {g.nome}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col lg={4}>
                  <Label>Città</Label>
                  <Input
                    name="citta"
                    placeholder="Città"
                    value={form.citta}
                    onChange={onChange}
                  />
                </Col>
                <Col lg={4}>
                  <Label>CAP</Label>
                  <Input
                    name="cap"
                    placeholder="CAP"
                    value={form.cap}
                    onChange={onChange}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>


        {slug && (
          <Col xl={12} className="mb-3">
            <Card>
              <CardBody>
                <Row className="g-3">
                  <Col xs={12} className="d-flex gap-2">
                    <SectionReferenti clienteID={slug} />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        )}



        <Col xl={12} className="mb-3">
          <Card>
            <CardBody>
              <Row className="g-3">
                <Col xs={12} className="d-flex gap-2">
                  <Btn
                    color="primary"
                    className="px-4"
                    type="submit"
                    disabled={!canSubmit || saving}
                  >
                    {saving ? (
                      <Spinner size="sm" />
                    ) : isEdit ? (
                      'Aggiorna'
                    ) : (
                      'Crea'
                    )}
                  </Btn>
                  <Link to="/gestione-clienti">
                    <Btn color="danger" className="px-4" type="button">
                      Annulla
                    </Btn>
                  </Link>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Form>
    </div>
  );
}
